import React, { useState, useEffect, useCallback } from 'react';
import { healthApi } from '../../services/api';
import toast from 'react-hot-toast';
import { formatDate } from '../../utils/date';

/* ─── Pulse animation injected once ─── */
const PULSE_CSS = `
  @keyframes bgPulse {
    0%   { box-shadow: 0 0 0 0 rgba(16,185,129,0.7); }
    70%  { box-shadow: 0 0 0 6px rgba(16,185,129,0); }
    100% { box-shadow: 0 0 0 0 rgba(16,185,129,0); }
  }
  @keyframes shimmer {
    0%   { background-position: -400px 0; }
    100% { background-position: 400px 0; }
  }
  .svc-card:hover { transform: translateY(-2px); box-shadow: 0 6px 24px rgba(0,0,0,0.35) !important; }
`;

/* ─── Colour helpers ─── */
const STATUS_COLOR = {
  online:  '#10b981',
  offline: '#ef4444',
  unknown: '#64748b',
};

function responseColor(ms) {
  if (ms == null) return '#64748b';
  if (ms < 100)  return '#10b981';
  if (ms < 300)  return '#3b82f6';
  if (ms < 1000) return '#f59e0b';
  return '#ef4444';
}

/* ─── Sub-components ─── */
function StatusDot({ status }) {
  const color = STATUS_COLOR[status] || STATUS_COLOR.unknown;
  return (
    <span style={{
      display: 'inline-block', width: 10, height: 10, borderRadius: '50%',
      background: color, marginRight: 6, flexShrink: 0,
      animation: status === 'online' ? 'bgPulse 2s infinite' : 'none',
      boxShadow: status === 'online' ? `0 0 6px ${color}` : 'none',
    }} />
  );
}

function Tag({ label, color }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', padding: '2px 9px',
      borderRadius: 5, background: color + '22', color, fontSize: 11,
      fontWeight: 700, letterSpacing: '0.04em',
    }}>{label}</span>
  );
}

function MetricRow({ label, value, mono = true }) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      padding: '5px 0', borderBottom: '1px solid var(--border)', fontSize: 12,
    }}>
      <span style={{ color: 'var(--text3)' }}>{label}</span>
      <span style={{ color: 'var(--text)', fontWeight: 600, fontFamily: mono ? 'monospace' : 'inherit' }}>{value}</span>
    </div>
  );
}

function MemBar({ used, total }) {
  if (!used || !total) return null;
  const pct = Math.round((used / total) * 100);
  const barColor = pct > 80 ? '#ef4444' : pct > 60 ? '#f59e0b' : '#10b981';
  return (
    <div style={{ marginTop: 4 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--text3)', marginBottom: 3 }}>
        <span>Yaddaş</span>
        <span style={{ fontFamily: 'monospace', color: 'var(--text)' }}>{used}MB / {total}MB ({pct}%)</span>
      </div>
      <div style={{ height: 5, borderRadius: 3, background: 'var(--bg3, #0a0e1a)', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: barColor, borderRadius: 3, transition: 'width 0.6s ease' }} />
      </div>
    </div>
  );
}

function SkeletonCard() {
  const shimmer = {
    background: 'linear-gradient(90deg, var(--card) 25%, var(--bg3,#0d1526) 50%, var(--card) 75%)',
    backgroundSize: '400px 100%',
    animation: 'shimmer 1.4s infinite',
    borderRadius: 8,
  };
  return (
    <div style={{
      background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 14, padding: 20,
    }}>
      <div style={{ ...shimmer, height: 16, width: '60%', marginBottom: 12 }} />
      <div style={{ ...shimmer, height: 12, width: '40%', marginBottom: 20 }} />
      {[1,2,3,4].map(i => (
        <div key={i} style={{ ...shimmer, height: 10, marginBottom: 8, width: i % 2 === 0 ? '80%' : '90%' }} />
      ))}
    </div>
  );
}

function ServiceCard({ svc }) {
  const borderColor = STATUS_COLOR[svc.status] || STATUS_COLOR.unknown;
  const statusLabel = svc.status === 'online' ? 'Online' : svc.status === 'offline' ? 'Offline' : 'Naməlum';

  return (
    <div className="svc-card" style={{
      background: 'var(--card)', border: `1px solid ${borderColor}`,
      borderRadius: 14, padding: 20, position: 'relative', overflow: 'hidden',
      transition: 'transform 0.2s, box-shadow 0.2s',
    }}>
      {/* Glow strip on top */}
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, height: 3,
        background: borderColor, opacity: 0.7,
      }} />

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, marginTop: 4 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <StatusDot status={svc.status} />
          <span style={{ fontWeight: 700, fontSize: 14, color: 'var(--text)' }}>{svc.name}</span>
        </div>
        <Tag label={statusLabel} color={borderColor} />
      </div>

      {/* Response time badge */}
      {svc.responseTimeMs != null && (
        <div style={{ marginBottom: 12 }}>
          <Tag
            label={`${svc.responseTimeMs}ms`}
            color={responseColor(svc.responseTimeMs)}
          />
        </div>
      )}

      {/* Metrics */}
      <div style={{ borderTop: '1px solid var(--border)', paddingTop: 10 }}>
        {svc.version    && <MetricRow label="Versiya"  value={`v${svc.version}`} />}
        {svc.uptimeHuman && <MetricRow label="Uptime"  value={svc.uptimeHuman} />}
        {svc.db         && (
          <MetricRow
            label="Verilənlər bazası"
            value={svc.db}
            mono={false}
          />
        )}
        {svc.port       && <MetricRow label="Port"     value={svc.port} />}
      </div>

      {/* Memory bar */}
      {svc.memory && <div style={{ marginTop: 10 }}><MemBar used={svc.memory.used} total={svc.memory.total} /></div>}

      {/* Error message */}
      {svc.status === 'offline' && svc.error && (
        <div style={{
          marginTop: 12, padding: '8px 10px', borderRadius: 7,
          background: '#ef444418', border: '1px solid #ef444440',
          fontSize: 11, color: '#ef4444', fontFamily: 'monospace',
          wordBreak: 'break-word',
        }}>
          {svc.error}
        </div>
      )}

      {/* Last checked */}
      <div style={{ marginTop: 10, fontSize: 10, color: 'var(--text3)', textAlign: 'right' }}>
        Son yoxlama: {formatDate(svc.lastChecked, 'timesec')}
      </div>
    </div>
  );
}

function GatewayCard({ gw }) {
  const statusColor = gw?.db === 'connected' ? '#10b981' : '#f59e0b';
  return (
    <div style={{
      background: 'linear-gradient(135deg, var(--card) 0%, #0d1526 100%)',
      border: '1px solid var(--primary, #3b82f6)',
      borderRadius: 16, padding: 24, marginBottom: 28,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
        {/* Left: identity */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{
            width: 52, height: 52, borderRadius: 14,
            background: 'linear-gradient(135deg,#3b82f6,#06b6d4)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 26, flexShrink: 0,
          }}>⬡</div>
          <div>
            <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--text)', marginBottom: 2 }}>
              BakuGate Gateway
              <span style={{
                marginLeft: 10, fontSize: 11, fontWeight: 700,
                background: '#10b98122', color: '#10b981', padding: '2px 8px', borderRadius: 4,
              }}>ONLINE</span>
            </div>
            <div style={{ fontSize: 12, color: 'var(--text3)' }}>
              {gw ? `v${gw.version || '1.0.0'}` : '...'} &nbsp;·&nbsp; Port {gw?.port || 3000}
            </div>
          </div>
        </div>

        {/* Right: quick stats */}
        {gw && (
          <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 20, fontWeight: 800, color: '#3b82f6' }}>{gw.uptimeHuman || '—'}</div>
              <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 2 }}>Uptime</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 20, fontWeight: 800, color: statusColor }}>{gw.db === 'connected' ? '✓' : '✗'}</div>
              <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 2 }}>Verilənlər Bazası</div>
            </div>
            {gw.dbPingMs != null && (
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 20, fontWeight: 800, color: responseColor(gw.dbPingMs) }}>{gw.dbPingMs}ms</div>
                <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 2 }}>DB Ping</div>
              </div>
            )}
            {gw.memory && (
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--text)' }}>{gw.memory.used}MB</div>
                <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 2 }}>Heap İstifadəsi</div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Memory bar for gateway */}
      {gw?.memory && (
        <div style={{ marginTop: 16 }}>
          <MemBar used={gw.memory.used} total={gw.memory.total} />
        </div>
      )}
    </div>
  );
}

function SummaryCard({ label, value, color }) {
  return (
    <div style={{
      background: 'var(--card)', border: `1px solid ${color}`,
      borderRadius: 10, padding: '12px 18px', flex: 1, minWidth: 120, textAlign: 'center',
    }}>
      <div style={{ fontSize: 26, fontWeight: 900, color }}>{value}</div>
      <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 3 }}>{label}</div>
    </div>
  );
}

/* ─── Main component ─── */
export default function Services() {
  const [gateway, setGateway]       = useState(null);
  const [services, setServices]     = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [countdown, setCountdown]   = useState(15);
  const [lastRefreshed, setLastRefreshed] = useState(null);

  const fetchHealth = useCallback(async (forceRefresh = false) => {
    setError(null);
    try {
      const [gwRes, svcRes] = await Promise.all([
        healthApi.getSelf(),
        healthApi.getServices(forceRefresh),
      ]);
      setGateway(gwRes);
      setServices(Array.isArray(svcRes) ? svcRes : []);
      setLastRefreshed(new Date());
      setCountdown(30);
    } catch (err) {
      setError('Monitorinq xidməti əlçatan deyil');
      toast.error('Monitorinq məlumatı alına bilmədi');
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial fetch
  useEffect(() => { fetchHealth(); }, [fetchHealth]);

  // Auto-refresh countdown
  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) { fetchHealth(false); return 30; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [autoRefresh, fetchHealth]);

  // Derived stats
  const onlineCount  = services.filter(s => s.status === 'online').length;
  const offlineCount = services.filter(s => s.status === 'offline').length;
  const avgResponse  = services.length
    ? Math.round(services.filter(s => s.responseTimeMs != null).reduce((a, s) => a + s.responseTimeMs, 0) / services.length)
    : 0;
  const allOnline = offlineCount === 0 && services.length > 0;

  return (
    <>
      {/* Inject CSS animations */}
      <style>{PULSE_CSS}</style>

      <div style={{ padding: 28, maxWidth: 1400 }}>
        {/* ─── Page header ─── */}
        <div style={{ marginBottom: 24 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
            <div>
              <h1 style={{ fontSize: 26, fontWeight: 900, color: 'var(--text)', margin: 0, marginBottom: 4 }}>
                Sistem Monitorinqi
              </h1>
              <p style={{ fontSize: 13, color: 'var(--text3)', margin: 0 }}>
                Bütün mikroservislərin real-time sağlamlıq vəziyyəti
              </p>
            </div>

            {/* Status pill + last refreshed */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
              <span style={{
                padding: '5px 14px', borderRadius: 20, fontSize: 12, fontWeight: 700,
                background: allOnline ? '#10b98122' : offlineCount > 0 ? '#ef444422' : '#64748b22',
                color: allOnline ? '#10b981' : offlineCount > 0 ? '#ef4444' : '#64748b',
                border: `1px solid ${allOnline ? '#10b981' : offlineCount > 0 ? '#ef4444' : '#64748b'}`,
              }}>
                {allOnline ? 'Hamısı Onlayn' : offlineCount > 0 ? `${offlineCount} servis offline` : loading ? 'Yüklənir...' : 'Naməlum'}
              </span>
              {lastRefreshed && (
                <span style={{ fontSize: 11, color: 'var(--text3)' }}>
                  Son yenilənmə: {formatDate(lastRefreshed, 'timesec')}
                </span>
              )}
            </div>
          </div>

          {/* ─── Action bar ─── */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 18, flexWrap: 'wrap' }}>
            <button
              onClick={() => { setLoading(true); fetchHealth(true); }}
              style={{
                background: 'var(--primary, #3b82f6)', color: '#fff', border: 'none',
                borderRadius: 8, padding: '8px 18px', fontWeight: 700, cursor: 'pointer', fontSize: 13,
              }}
            >
              Yenilə
            </button>

            {/* Auto-refresh toggle */}
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <div
                onClick={() => setAutoRefresh(a => !a)}
                style={{
                  width: 38, height: 20, borderRadius: 10, position: 'relative', cursor: 'pointer',
                  background: autoRefresh ? 'var(--primary, #3b82f6)' : 'var(--bg3, #1a2340)',
                  border: '1px solid var(--border)', transition: 'background 0.2s',
                }}
              >
                <div style={{
                  position: 'absolute', top: 2, left: autoRefresh ? 18 : 2,
                  width: 14, height: 14, borderRadius: '50%', background: '#fff',
                  transition: 'left 0.2s',
                }} />
              </div>
              <span style={{ fontSize: 12, color: 'var(--text3)' }}>
                Avtomatik yenilə
              </span>
            </label>

            {autoRefresh && (
              <span style={{ fontSize: 12, color: 'var(--text3)', fontFamily: 'monospace' }}>
                {countdown}s sonra yenilənəcək
              </span>
            )}
          </div>
        </div>

        {/* ─── Error state ─── */}
        {error && !loading && (
          <div style={{
            background: '#ef444418', border: '1px solid #ef444460', borderRadius: 12,
            padding: 24, textAlign: 'center', marginBottom: 24,
          }}>
            <div style={{ fontSize: 16, color: '#ef4444', fontWeight: 700, marginBottom: 8 }}>{error}</div>
            <button
              onClick={() => { setLoading(true); setError(null); fetchHealth(true); }}
              style={{ background: '#ef4444', color: '#fff', border: 'none', borderRadius: 7, padding: '8px 18px', cursor: 'pointer', fontWeight: 700 }}
            >
              Yenidən cəhd et
            </button>
          </div>
        )}

        {/* ─── Gateway card ─── */}
        {!error && (loading && !gateway ? (
          <div style={{
            background: 'linear-gradient(135deg, var(--card) 0%, #0d1526 100%)',
            border: '1px solid var(--primary)', borderRadius: 16, padding: 24, marginBottom: 28,
            display: 'flex', alignItems: 'center', gap: 16,
          }}>
            <div style={{ width: 52, height: 52, borderRadius: 14, background: 'var(--bg3)', animation: 'shimmer 1.4s infinite', backgroundSize: '400px 100%' }} />
            <div style={{ flex: 1 }}>
              <div style={{ height: 18, width: '30%', background: 'var(--bg3)', borderRadius: 6, animation: 'shimmer 1.4s infinite', backgroundSize: '400px 100%', marginBottom: 8 }} />
              <div style={{ height: 12, width: '20%', background: 'var(--bg3)', borderRadius: 6, animation: 'shimmer 1.4s infinite', backgroundSize: '400px 100%' }} />
            </div>
          </div>
        ) : (
          <GatewayCard gw={gateway} />
        ))}

        {/* ─── Summary row ─── */}
        {!loading && !error && services.length > 0 && (
          <div style={{ display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
            <SummaryCard label="Ümumi Servis"   value={services.length}  color="var(--primary, #3b82f6)" />
            <SummaryCard label="Online"          value={onlineCount}      color="#10b981" />
            <SummaryCard label="Offline"         value={offlineCount}     color="#ef4444" />
            <SummaryCard label="Ort. Cavab"      value={`${avgResponse}ms`} color={responseColor(avgResponse)} />
          </div>
        )}

        {/* ─── Services grid ─── */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
          gap: 16,
        }}>
          {loading
            ? [1, 2, 3, 4].map(i => <SkeletonCard key={i} />)
            : services.map(svc => <ServiceCard key={svc.key} svc={svc} />)
          }
        </div>

        {/* Empty state */}
        {!loading && !error && services.length === 0 && (
          <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--text3)' }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>📡</div>
            <div style={{ fontSize: 15, fontWeight: 600 }}>Heç bir servis tapılmadı</div>
            <div style={{ fontSize: 12, marginTop: 6 }}>BARRIER_SERVICE_URL, RESTAURANT_SERVICE_URL və s. env dəyişənlərini yoxlayın</div>
          </div>
        )}
      </div>
    </>
  );
}
